import React, { useState } from 'react';
import axios from 'axios';
import styles from './ApiTester.module.css';

const ApiTester = ({ endpoints, backendUrl }) => {
    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [requestBody, setRequestBody] = useState('');
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savedRequests, setSavedRequests] = useState(
        JSON.parse(localStorage.getItem('savedApiRequests') || '[]')
    );
    const [requestName, setRequestName] = useState('');
    
    const handleSelectEndpoint = (endpoint) => {
        setSelectedEndpoint(endpoint);
        if (endpoint.body) {
            setRequestBody(JSON.stringify(endpoint.body, null, 2));
        } else {
            setRequestBody('');
        }
        setResponse(null);
        setError(null);
    };
    
    const handleRequestBodyChange = (e) => {
        setRequestBody(e.target.value);
    };

    const handleSaveRequest = () => {
        if (!selectedEndpoint || !requestName.trim()) return;
        
        const newRequest = {
            id: Date.now().toString(),
            name: requestName,
            endpoint: selectedEndpoint,
            body: requestBody
        };
        
        const updated = [...savedRequests, newRequest];
        setSavedRequests(updated);
        localStorage.setItem('savedApiRequests', JSON.stringify(updated));
        setRequestName('');
    };
    
    const handleLoadRequest = (request) => {
        setSelectedEndpoint(request.endpoint);
        setRequestBody(request.body);
    };
    
    const handleDeleteRequest = (id) => {
        const updated = savedRequests.filter(req => req.id !== id);
        setSavedRequests(updated);
        localStorage.setItem('savedApiRequests', JSON.stringify(updated));
    };
    
    const handleSendRequest = async () => {
        if (!selectedEndpoint) return;
        
        try {
            setLoading(true);
            setError(null);
            
            let requestUrl = selectedEndpoint.url;
            let requestData = {};
            
            // Parse request body if available
            if (requestBody) {
                try {
                    requestData = JSON.parse(requestBody);
                    
                    // Generate random mobile number if not provided and this is create-game endpoint
                    if (selectedEndpoint.url === '/api/create-game' && !requestData.mob) {
                        requestData.mob = Math.floor(1000000000 + Math.random() * 9000000000).toString();
                    }
                } catch (e) {
                    setError({
                        message: 'Invalid JSON in request body',
                        response: e.message
                    });
                    setLoading(false);
                    return;
                }
            }
            
            // Handle URL parameters if needed
            if (selectedEndpoint.url.includes(':apikey') && requestData.apikey) {
                requestUrl = requestUrl.replace(':apikey', requestData.apikey);
            } else if (selectedEndpoint.url.includes(':apikey')) {
                // Try to get an apikey from the request body
                requestUrl = requestUrl.replace(':apikey', requestData.apikey || 'missing-key');
            }
            
            if (selectedEndpoint.url.includes(':roomid') && requestData.roomId) {
                requestUrl = requestUrl.replace(':roomid', requestData.roomId);
            } else if (selectedEndpoint.url.includes(':roomid')) {
                // Try to get a roomId from the request body
                requestUrl = requestUrl.replace(':roomid', requestData.roomId || 'missing-id');
            }
            // Generic :id support for room routes (e.g., /api/rooms/:id/features)
            if (selectedEndpoint.url.includes(':id') && (requestData.id || requestData.roomId)) {
                requestUrl = requestUrl.replace(':id', requestData.id || requestData.roomId);
            } else if (selectedEndpoint.url.includes(':id')) {
                requestUrl = requestUrl.replace(':id', requestData.id || 'missing-id');
            }
            
            // Prepend the backend URL
            requestUrl = backendUrl + requestUrl;
            
            let result;
            if (selectedEndpoint.method === 'GET') {
                result = await axios.get(requestUrl);
            } else if (selectedEndpoint.method === 'POST') {
                result = await axios.post(requestUrl, requestData);
            } else if (selectedEndpoint.method === 'PUT') {
                result = await axios.put(requestUrl, requestData);
            } else if (selectedEndpoint.method === 'DELETE') {
                result = await axios.delete(requestUrl);
            }
            
            setResponse({
                status: result.status,
                data: result.data
            });
            
            // If this was a create-game request and succeeded, show join button
            if (selectedEndpoint.url === '/api/create-game' && result.data.status === 'success') {
                setResponse({
                    ...result.data,
                    status: result.status,
                    data: result.data,
                    showJoinButton: true,
                    room_url: result.data.room_url,
                    roomId: result.data.roomId,
                    password: result.data.password
                });
            }
        } catch (err) {
            setError({
                message: err.message,
                response: err.response?.data || 'No response data'
            });
        } finally {
            setLoading(false);
        }
    };
    
    const handleJoinGame = () => {
        // Extract just the roomId part from the URL or use directly from response data
        const roomId = response.data.roomId || response.room_url.split('/').pop();
        const password = response.data.password || '';
        
        // Include password as a query parameter
        window.open(`/join/${roomId}?password=${encodeURIComponent(password)}`, '_blank');
    };
    
    const formatMethodBadge = (method) => {
        const colors = {
            GET: '#2ecc71',
            POST: '#e67e22',
            PUT: '#3498db',
            DELETE: '#e74c3c'
        };
        
        return (
            <span className={styles.methodBadge} style={{ backgroundColor: colors[method] }}>
                {method}
            </span>
        );
    };
    
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(
            () => {
                // Flash some indication of success
                const el = document.getElementById('clipboardMessage');
                if (el) {
                    el.style.opacity = 1;
                    setTimeout(() => { el.style.opacity = 0; }, 2000);
                }
            }
        );
    };
    
    return (
        <div className={styles.apiTester}>
            <div className={styles.sidebar}>
                <div className={styles.endpointList}>
                    <h3>Available Endpoints</h3>
                    <ul>
                        {endpoints.map((endpoint, index) => (
                            <li 
                                key={index} 
                                className={selectedEndpoint === endpoint ? styles.selected : ''}
                                onClick={() => handleSelectEndpoint(endpoint)}
                            >
                                <span className={styles.method}>{endpoint.method}</span>
                                <span className={styles.url}>{endpoint.url}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                
                {savedRequests.length > 0 && (
                    <div className={styles.savedRequests}>
                        <h3>Saved Requests</h3>
                        <ul>
                            {savedRequests.map((req) => (
                                <li key={req.id} className={styles.savedRequest}>
                                    <div className={styles.savedRequestInfo} onClick={() => handleLoadRequest(req)}>
                                        {formatMethodBadge(req.endpoint.method)}
                                        <span className={styles.savedRequestName}>{req.name}</span>
                                    </div>
                                    <button 
                                        className={styles.deleteButton}
                                        onClick={() => handleDeleteRequest(req.id)}
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            
            {selectedEndpoint && (
                <div className={styles.testPanel}>
                    <div className={styles.endpointDetails}>
                        <h3>{selectedEndpoint.name}</h3>
                        <p>{selectedEndpoint.description}</p>
                        <div className={styles.httpDetails}>
                            {formatMethodBadge(selectedEndpoint.method)}
                            <span className={styles.endpointUrl}>{selectedEndpoint.url}</span>
                        </div>
                        <div className={styles.fullUrl}>
                            <span>Full URL: </span>
                            <code>{backendUrl}{selectedEndpoint.url}</code>
                            <button 
                                className={styles.copyButton}
                                onClick={() => copyToClipboard(`${backendUrl}${selectedEndpoint.url}`)}
                                title="Copy to clipboard"
                            >
                                Copy
                            </button>
                            <span id="clipboardMessage" className={styles.copyMessage}>Copied!</span>
                        </div>
                    </div>
                    
                    {(selectedEndpoint.method === 'POST' || selectedEndpoint.method === 'PUT' || selectedEndpoint.url.includes(':')) && (
                        <div className={styles.requestBody}>
                            <div className={styles.requestHeader}>
                                <h4>Request Body / Parameters</h4>
                                <div className={styles.saveRequestForm}>
                                    <input
                                        type="text"
                                        value={requestName}
                                        onChange={(e) => setRequestName(e.target.value)}
                                        placeholder="Save this request as..."
                                        className={styles.saveRequestInput}
                                    />
                                    <button 
                                        onClick={handleSaveRequest}
                                        className={styles.saveButton}
                                        disabled={!requestName.trim()}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                            <textarea 
                                value={requestBody} 
                                onChange={handleRequestBodyChange} 
                                placeholder="Enter JSON request body"
                            />
                        </div>
                    )}
                    
                    <button 
                        className={styles.sendButton} 
                        onClick={handleSendRequest}
                        disabled={loading}
                    >
                        {loading ? 'Sending...' : 'Send Request'}
                    </button>
                    
                    {error && (
                        <div className={styles.errorResponse}>
                            <h4>Error</h4>
                            <p>{error.message}</p>
                            <pre>{JSON.stringify(error.response, null, 2)}</pre>
                        </div>
                    )}
                    
                    {response && (
                        <div className={styles.response}>
                            <h4>Response</h4>
                            <div className={styles.statusCode}>
                                Status: <span>{response.status}</span>
                            </div>
                            <div className={styles.responseBody}>
                                <pre>{JSON.stringify(response.data, null, 2)}</pre>
                                <button 
                                    className={styles.copyButton}
                                    onClick={() => copyToClipboard(JSON.stringify(response.data, null, 2))}
                                    title="Copy to clipboard"
                                >
                                    Copy Response
                                </button>
                            </div>
                            
                            {response.showJoinButton && response.roomId && (
                                <div className={styles.joinGameContainer}>
                                    <button 
                                        className={styles.joinGameButton}
                                        onClick={handleJoinGame}
                                    >
                                        Join This Game
                                    </button>
                                    <div className={styles.joinInstructions}>
                                        <p>Copy this link to share with others:</p>
                                        <div className={styles.urlContainer}>
                                            <input 
                                                type="text" 
                                                readOnly 
                                                value={`${window.location.origin}/join/${response.data.roomId}?password=${encodeURIComponent(response.data.password || '')}`} 
                                                onClick={(e) => e.target.select()} 
                                                className={styles.urlInput}
                                            />
                                            <button 
                                                className={styles.copyButton}
                                                onClick={() => copyToClipboard(`${window.location.origin}/join/${response.data.roomId}?password=${encodeURIComponent(response.data.password || '')}`)}
                                            >
                                                Copy
                                            </button>
                                        </div>
                                        <p className={styles.note}>Password: {response.data.password}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ApiTester;
